import { CircularProgress } from "@mui/material";
import {
    SovosBox,
    SovosButton,
    SovosCountryFlag,
    SovosMenuItem,
    SovosSelect,
    SovosTextField,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome } from "mosaic-react-icons";
import React, { useCallback, useRef, useState } from "react";
import { trackUserAction } from "../../../../../utils/pendo";
import AiConfidencePill from "./AiConfidencePill";
import ChooseEntityDialogFrame from "./ChooseEntityDialogFrame";
import EmptyMappingContexts from "./EmptyMappingContexts";
import EntityAiSuggestions from "./EntityAiSuggestions";
import InlineLoadError from "./InlineLoadError";
import useMappingContext from "./useMappingContext";

const ChooseEntityCard = ({
    disableCancel = false,
    i18n,
    initialConfigSet,
    initialEntityName,
    initialFileType,
    onCancel,
    onFetchEntities,
    onFetchMappingContexts,
    onNext,
    onRetryPreload,
    onSuggestEntity,
    preloadError = false,
    profileId,
    projectId,
    projectModules,
}) => {
    const [aiEntityScore, setAiEntityScore] = useState(null);
    const [aiEntitySuggestions, setAiEntitySuggestions] = useState([]);
    const [aiEntityWarnings, setAiEntityWarnings] = useState([]);
    const [loadingAiEntity, setLoadingAiEntity] = useState(false);
    const aiRequestId = useRef(0);
    const invalidateAiState = useCallback(() => {
        aiRequestId.current += 1;
        setAiEntityScore(null);
        setAiEntitySuggestions([]);
        setAiEntityWarnings([]);
        setLoadingAiEntity(false);
    }, []);
    const mapping = useMappingContext({
        initialConfigSet,
        initialEntityName,
        initialFileType,
        onContextReset: invalidateAiState,
        onFetchEntities,
        onFetchMappingContexts,
        projectId,
        projectModules,
    });
    const selectedContext = mapping.contexts.find(
        (context) => context.country_code === mapping.countryCode,
    );
    const structures = selectedContext?.legal_structures || [];
    const noMappableEntities =
        mapping.selectedStructure?.mappable_entity_count === 0 ||
        Boolean(
            mapping.legalStructureId &&
            !mapping.loadingEntities &&
            !mapping.entityError &&
            !mapping.entities.length,
        );
    const canProceed = Boolean(
        mapping.fileType &&
        mapping.entities.some((entity) => entity.file_type === mapping.fileType),
    );

    const handleSuggestEntity = async () => {
        if (!profileId || !mapping.configSet || !onSuggestEntity || !mapping.entities.length) return;
        const currentRequest = ++aiRequestId.current;
        trackUserAction("mapping_entity_suggestion_requested", {
            profile_id: profileId,
            config_set: mapping.configSet,
            available_entity_count: mapping.entities.length,
        });
        setLoadingAiEntity(true);
        setAiEntitySuggestions([]);
        setAiEntityWarnings([]);
        try {
            const result = await onSuggestEntity(profileId, mapping.configSet, mapping.entities);
            if (currentRequest !== aiRequestId.current) return;
            setAiEntitySuggestions(result?.suggestions || []);
            setAiEntityWarnings(result?.warnings || []);
        } finally {
            if (currentRequest === aiRequestId.current) setLoadingAiEntity(false);
        }
    };

    const handleEntityChange = (value) => {
        mapping.selectEntity(value);
        setAiEntityScore(null);
    };

    const handleSelectAiSuggestion = (value, score) => {
        const selectedEntity = mapping.entities.find((entity) => entity.file_type === value);
        const suggestionRank = aiEntitySuggestions.findIndex(
            (suggestion) => suggestion.entity_name === selectedEntity?.cdm_entity_name,
        );
        const suggestion = aiEntitySuggestions[suggestionRank];
        if (suggestion) {
            trackUserAction("mapping_entity_suggestion_accepted", {
                suggested_entity: suggestion.entity_name,
                suggestion_score: suggestion.score,
                suggestion_rank: suggestionRank,
            });
        }
        handleEntityChange(value);
        setAiEntityScore(score);
    };

    const handleClose = () => {
        invalidateAiState();
        onCancel();
    };

    return (
        <ChooseEntityDialogFrame
            disableClose={disableCancel}
            hasAi={Boolean(onSuggestEntity)}
            i18n={i18n}
            onClose={handleClose}
        >
            <SovosSelect
                disabled={mapping.loadingContexts || mapping.contextError || mapping.hasNoContexts}
                fullWidth
                label={i18n`Country`}
                onChange={(event) => mapping.selectCountry(event.target.value)}
                sx={{ mb: 2.5 }}
                value={mapping.countryCode}
            >
                <SovosMenuItem value="">
                    <em>{i18n`Select country`}</em>
                </SovosMenuItem>
                {mapping.contexts.map((context) => (
                    <SovosMenuItem key={context.country_code} value={context.country_code}>
                        <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                            <SovosCountryFlag
                                flagHeight="20px"
                                countryCode={context.country_code}
                            />
                            <SovosTypography variant="body2">
                                {context.country_code}
                            </SovosTypography>
                        </SovosBox>
                    </SovosMenuItem>
                ))}
            </SovosSelect>
            {mapping.loadingContexts && (
                <CircularProgress aria-label={i18n`Loading countries`} size={20} />
            )}
            {mapping.contextError && <InlineLoadError i18n={i18n} onRetry={mapping.loadContexts} />}
            {mapping.hasNoContexts && (
                <EmptyMappingContexts i18n={i18n} onRetry={mapping.loadContexts} />
            )}

            {structures.length === 1 && mapping.legalStructureId ? (
                <SovosTextField
                    fullWidth
                    inputProps={{ "aria-readonly": true }}
                    label={i18n`Legal structure`}
                    readOnly
                    sx={{ mb: 2.5 }}
                    value={mapping.selectedStructure?.label || ""}
                />
            ) : (
                <SovosSelect
                    disabled={!mapping.countryCode || structures.length < 2}
                    fullWidth
                    label={i18n`Legal structure`}
                    onChange={(event) => mapping.selectStructure(event.target.value)}
                    required
                    sx={{ mb: 2.5 }}
                    value={mapping.legalStructureId}
                >
                    <SovosMenuItem value="">
                        <em>{i18n`Select legal structure`}</em>
                    </SovosMenuItem>
                    {structures.map((structure) => (
                        <SovosMenuItem key={structure.id} value={structure.id}>
                            {structure.label}
                        </SovosMenuItem>
                    ))}
                </SovosSelect>
            )}

            <SovosSelect
                disabled={
                    !mapping.legalStructureId ||
                    mapping.loadingEntities ||
                    mapping.entityError ||
                    noMappableEntities
                }
                fullWidth
                label={i18n`Entity`}
                onChange={(event) => handleEntityChange(event.target.value)}
                renderValue={(selected) => (
                    <SovosBox
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <SovosTypography variant="body2">
                            {mapping.entities.find((entity) => entity.file_type === selected)
                                ?.entity_name || selected}
                        </SovosTypography>
                        {aiEntityScore !== null && <AiConfidencePill score={aiEntityScore} />}
                    </SovosBox>
                )}
                sx={{ mb: 1.5 }}
                value={mapping.fileType}
            >
                <SovosMenuItem value="">
                    <em>{i18n`Select entity`}</em>
                </SovosMenuItem>
                {mapping.entities.map((entity) => (
                    <SovosMenuItem key={entity.file_type} value={entity.file_type}>
                        {entity.entity_name}
                    </SovosMenuItem>
                ))}
            </SovosSelect>
            {mapping.loadingEntities && (
                <CircularProgress aria-label={i18n`Loading entities`} size={20} />
            )}
            {mapping.entityError && <InlineLoadError i18n={i18n} onRetry={mapping.retryEntities} />}
            {preloadError && <InlineLoadError i18n={i18n} onRetry={onRetryPreload} />}
            {noMappableEntities && (
                <SovosTypography role="status" color="text.secondary" variant="caption">
                    {i18n`No mappable entities are configured for ${mapping.selectedStructure?.label || ""}`}
                </SovosTypography>
            )}
            {mapping.unresolved && (
                <SovosBox role="alert" sx={{ mb: 2 }}>
                    <SovosTypography color="warning.dark" variant="caption">
                        {i18n`The saved mapping configuration could not be resolved.`}
                    </SovosTypography>
                    <SovosTypography variant="caption">
                        {`${mapping.entityName || initialEntityName} (${initialFileType})`}
                    </SovosTypography>
                    <SovosButton onClick={mapping.loadContexts} size="small" variant="text">
                        {i18n`Retry`}
                    </SovosButton>
                </SovosBox>
            )}

            {onSuggestEntity && (
                <SovosBox sx={{ mb: 3, mt: 1.5 }}>
                    <SovosButton
                        disabled={
                            !mapping.legalStructureId ||
                            !mapping.entities.length ||
                            loadingAiEntity ||
                            noMappableEntities
                        }
                        fullWidth
                        onClick={handleSuggestEntity}
                        size="small"
                        startIcon={
                            loadingAiEntity ? <CircularProgress size={14} /> : <AutoAwesome />
                        }
                        variant="outlined"
                    >
                        {i18n`Map by AI`}
                    </SovosButton>
                    <EntityAiSuggestions
                        entities={mapping.entities}
                        fileType={mapping.fileType}
                        onSelect={handleSelectAiSuggestion}
                        suggestions={aiEntitySuggestions}
                        warnings={aiEntityWarnings}
                    />
                </SovosBox>
            )}

            <SovosBox sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <SovosButton disabled={disableCancel} onClick={handleClose} variant="text">
                    {i18n`Cancel`}
                </SovosButton>
                <SovosButton
                    disabled={!canProceed || disableCancel}
                    onClick={() =>
                        onNext({
                            configSet: mapping.configSet,
                            entityLabel: mapping.entityName,
                            entityName: mapping.entityName,
                            fileType: mapping.fileType,
                        })
                    }
                    variant="contained"
                >
                    {i18n`Next`}
                </SovosButton>
            </SovosBox>
        </ChooseEntityDialogFrame>
    );
};

export default ChooseEntityCard;
